import { getProductPromotionType } from '~/features/basket/utils/getProductPromotionType';
import { Product, PromotionType } from '~/lib/data-contract/salesforce';
import { useAppliedProductSpecialPrice } from '~/services/special-price';
import { useUtils } from '~/shared/hooks/useUtils';
import { useTranslation } from '~/shared/utils/translation';
import { StyledPricePerItem, StyledProductInfoPricePromoQt, StyledSpanStrike } from '../styled';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';

type Props = {
    lineItem: Product;
};

export const ProductPricePerItem = ({ lineItem }: Props) => {
    const { isPlusMember } = useCustomer();
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const productPromotionType = getProductPromotionType(lineItem);

    const { hasMetRequirements, isActive: hasSpecialPricing } =
        useAppliedProductSpecialPrice(lineItem);

    const isChristmasPrice = productPromotionType === PromotionType.CHRISTMAS_PRICE;

    const hasPromotion =
        (hasSpecialPricing && hasMetRequirements) ||
        (productPromotionType === PromotionType.MEMBER_PRICE && isPlusMember) ||
        productPromotionType === PromotionType.DISCOUNT_PRICE ||
        productPromotionType === PromotionType.MULTI_BUY ||
        productPromotionType === PromotionType.MIX_AND_MATCH ||
        isChristmasPrice;

    /**
     * Christmas keeps the retail "before" price on `c_promotionType.unitPrice`.
     * For those, `basePrice` can already be the discounted unit price.
     */
    const beforePrice =
        isChristmasPrice && lineItem.c_promotionType?.unitPrice != null
            ? lineItem.c_promotionType.unitPrice
            : lineItem.basePrice;

    /**
     * We will only show the discounted price if we have an ongoing promotion and if unit price is different from base price.
     * A non-strict comparison (!=) is used to compare string and numbers.
     */
    const shouldShowDiscountedPrice = hasPromotion && lineItem.c_unitPrice != beforePrice;

    return (
        <StyledPricePerItem>
            {shouldShowDiscountedPrice ? (
                <StyledProductInfoPricePromoQt>
                    <span>
                        {convertPriceToLocale(lineItem.c_unitPrice)}
                        <span> {translate('basket.perItem')}</span>
                    </span>
                    <StyledSpanStrike>{convertPriceToLocale(beforePrice)}</StyledSpanStrike>
                </StyledProductInfoPricePromoQt>
            ) : (
                <span>
                    <span>{convertPriceToLocale(lineItem.c_unitPrice)}</span>
                    <span> {translate('basket.perItem')}</span>
                </span>
            )}
        </StyledPricePerItem>
    );
};
